<script lang="ts">
import type {
  KaListboxOptionProps,
  KaListboxOptionPropsInternal,
  KaListboxOptionValueType,
} from './types';

export type { KaListboxOptionProps, KaListboxOptionValueType } from './types';
</script>
<script
  setup
  lang="ts"
  generic="T extends KaListboxOptionValueType, ID extends string | number | undefined"
>
import { ListboxOption } from '@headlessui/vue';
import { computed } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';
import type { NoInfer } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';
import IconCheck from '~icons/ion/checkmark';

import { injectListboxContext } from './context';

defineOptions({
  name: 'KaListboxOption',
  inheritAttrs: false,
});

type IDValidator = {
  value: undefined extends NoInfer<ID>
    ? NoInfer<T> extends object
      ? '`id` is required for object or array values'
      : NoInfer<T>
    : NoInfer<T>;
};

type Props = KaListboxOptionPropsInternal<T, ID> & IDValidator & AnyOtherAttr;

type AnyOtherAttr = {
  [key: string]: unknown;
};

const props = defineProps<Props>() as KaListboxOptionProps<KaListboxOptionValueType>;

const kaListboxContext = injectListboxContext();
const listboxDataTestid = kaListboxContext?.dataTestid?.value;
const { dataTestid, suffix } = useDataTestid(`${listboxDataTestid}-option-${props.label}`);

const open = computed(() => kaListboxContext?.isOpen.value);
</script>

<template>
  <ListboxOption
    v-if="open"
    :id="props.id?.toString()"
    :key="props.value?.toString()"
    v-slot="{ active, selected }"
    as="template"
    :value="props.value"
    :disabled="props.disabled"
  >
    <li
      v-bind="$attrs"
      :data-testid="dataTestid"
      :class="
        cnMerge([
          'relative m-0 cursor-pointer select-none list-none py-2 pl-3 pr-2',
          {
            'bg-gray-100 dark:bg-navy-800': active && !selected,
            'bg-blue-50 dark:bg-blue-400/20': selected,
            'cursor-not-allowed': props.disabled,
            'pt-1.5': ($slots.left || props.left) && (props.description || $slots.description),
          },
          props.containerClasses,
        ])
      "
    >
      <div :class="['flex w-full items-start justify-between', { 'opacity-50': props.disabled }]">
        <span
          v-if="$slots.left || props.left"
          class="mr-2 inline-flex flex-shrink-0 flex-grow-0 items-center justify-center [&>svg]:block [&>svg]:align-baseline"
          aria-hidden="true"
        >
          <slot name="left">
            <div
              :class="
                cnMerge('flex size-6 items-center justify-center', props.left?.wrapperClasses)
              "
            >
              <component
                :is="props.left.icon"
                v-if="props.left"
                :class="cnMerge('size-3.5', props.left.classes)"
              />
            </div>
          </slot>
        </span>

        <div
          class="flex-grow-1 mr-3 flex w-full flex-col break-all"
          :class="{ 'mt-[1px]': $slots.left || props.left }"
        >
          <div :class="cnMerge('flex items-center justify-between', props.labelContainerClasses)">
            <p
              v-if="$slots.default || props.label"
              :class="[
                'm-0 flex text-gray-900 dark:text-gray-50',
                selected ? 'font-semibold' : 'font-normal',
              ]"
              :title="props.label"
            >
              <slot :label="props.label">
                {{ props.label }}
              </slot>
            </p>

            <span
              v-if="$slots.right || props.right"
              :data-testid="suffix('right')"
              class="inline-flex flex-shrink-0 flex-grow-0 items-center justify-center [&>svg]:block [&>svg]:align-baseline"
              aria-hidden="true"
            >
              <slot name="right">
                <component
                  :is="props.right.icon"
                  v-if="props.right"
                  :class="cnMerge('size-3.5', props.right.classes)"
                />
              </slot>
            </span>
          </div>

          <p
            v-if="props.description || $slots.description"
            class="mx-0 mb-0 mt-1 flex text-gray-600 dark:text-gray-300"
          >
            <slot name="description">{{ props.description }}</slot>
          </p>
        </div>

        <span :data-testid="suffix('check')" class="h-5 w-5 flex-shrink-0 flex-grow-0">
          <IconCheck
            v-if="selected"
            class="m-0 h-full w-full p-0 text-gray-900 dark:text-gray-50"
            aria-hidden="true"
          />
        </span>
      </div>
    </li>
  </ListboxOption>
</template>
